import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getTableActionsConfig } from '../../config/appConfig';
import { ArrowUpIcon, SortBothIcon, ClipboardIcon } from '../ui/Icons';

// Table density configurations
const DENSITY_CONFIG = {
  compact: {
    name: 'Compact',
    cardPadding: 'p-3',
    gap: 'gap-2',
    fontSize: 'text-xs'
  },
  normal: {
    name: 'Normal', 
    cardPadding: 'p-4',
    gap: 'gap-3',
    fontSize: 'text-sm'
  },
  comfortable: {
    name: 'Comfortable',
    cardPadding: 'p-5',
    gap: 'gap-4',
    fontSize: 'text-sm'
  }
};

const CardAdvancedTable = ({ 
  columns = [], 
  data = [], 
  density = 'normal',
  onDensityChange,
  tableId = 'default-card-table',
  className,
  maxHeight = '600px',
  externalColumnWidths,
  externalColumnOrder,
  externalSortConfig,
  onConfigChange,
  showHeader = true,
  showDensityControl = true,
  rowPositioning = 'auto',
  customRowOrder = [],
  columnPositioning = 'auto'
}) => {
  const { theme } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState(columns.map(col => col.key));
  const [sortConfig, setSortConfig] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastExternalSortRef = useRef(null);


  // Load saved column configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem(`card-table-config-${tableId}`);
    if (savedConfig) {
      try {
        const { widths, order, sortConfig: savedSortConfig } = JSON.parse(savedConfig);
        if (widths) setColumnWidths(widths);
        if (order && order.length === columns.length) setColumnOrder(order);
        if (savedSortConfig) setSortConfig(savedSortConfig);
      } catch (error) {
        console.warn('Failed to load card table configuration:', error);
      }
    }
    setIsInitialized(true);
  }, [tableId, columns.length]);

  // Save column configuration to localStorage
  const saveConfiguration = useCallback(() => {
    const config = {
      widths: columnWidths,
      order: columnOrder,
      sortConfig: sortConfig
    };
    localStorage.setItem(`card-table-config-${tableId}`, JSON.stringify(config));
  }, [columnWidths, columnOrder, sortConfig, tableId]);

  // Handle external configuration updates
  useEffect(() => {
    if (isInitialized && externalColumnWidths && JSON.stringify(externalColumnWidths) !== JSON.stringify(columnWidths)) {
      setColumnWidths(externalColumnWidths);
    }
  }, [isInitialized, externalColumnWidths, columnWidths]);

  useEffect(() => {
    if (isInitialized && externalColumnOrder && externalColumnOrder.length > 0 && JSON.stringify(externalColumnOrder) !== JSON.stringify(columnOrder)) {
      setColumnOrder(externalColumnOrder);
    }
  }, [isInitialized, externalColumnOrder, columnOrder]);

  useEffect(() => {
    if (!isInitialized) return;
    const next = externalSortConfig ?? [];
    const serialized = JSON.stringify(next);
    if (lastExternalSortRef.current === serialized) return;
    lastExternalSortRef.current = serialized;
    setSortConfig(next);
  }, [isInitialized, externalSortConfig]);

  // Notify parent of configuration changes
  useEffect(() => {
    if (isInitialized && onConfigChange) {
      const timer = setTimeout(() => {
        onConfigChange({
          columnWidths,
          columnOrder,
          sortConfig
        });
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isInitialized, columnWidths, columnOrder, sortConfig, onConfigChange]);

  // Save configuration when it changes
  useEffect(() => {
    const timer = setTimeout(saveConfiguration, 500);
    return () => clearTimeout(timer);
  }, [saveConfiguration]);

  // Handle column sorting
  const handleSort = (columnKey, event) => {
    const isMultiSort = event.ctrlKey || event.metaKey;
    
    setSortConfig(prev => {
      if (!isMultiSort) {
        const existingSort = prev.find(s => s.key === columnKey);
        if (existingSort) {
          if (existingSort.direction === 'asc') {
            return [{ key: columnKey, direction: 'desc', priority: 1 }];
          } else {
            return [];
          }
        } else {
          return [{ key: columnKey, direction: 'asc', priority: 1 }];
        }
      } else {
        const existingIndex = prev.findIndex(s => s.key === columnKey);
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          if (existing.direction === 'asc') {
            const newConfig = [...prev];
            newConfig[existingIndex] = { ...existing, direction: 'desc' };
            return newConfig;
          } else {
            return prev.filter(s => s.key !== columnKey)
              .map((s, index) => ({ ...s, priority: index + 1 }));
          }
        } else {
          return [...prev, { key: columnKey, direction: 'asc', priority: prev.length + 1 }];
        }
      }
    });
  };

  // Sort data based on current sort configuration and positioning rules
  const sortedData = useMemo(() => {
    let processedData = [...data];
    
    // Apply custom row ordering if specified
    if (rowPositioning === 'custom' && customRowOrder.length > 0) {
      const orderMap = new Map(customRowOrder.map((id, index) => [id, index]));
      processedData.sort((a, b) => {
        const aOrder = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
    }
    
    // Apply sorting if configured
    if (sortConfig.length > 0) {
      processedData.sort((a, b) => {
        for (const sort of sortConfig.sort((a, b) => a.priority - b.priority)) {
          const aVal = a[sort.key];
          const bVal = b[sort.key];
          
          let comparison = 0;
          
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.localeCompare(bVal, undefined, { numeric: true });
          } else if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal;
          } else if (aVal instanceof Date && bVal instanceof Date) {
            comparison = aVal.getTime() - bVal.getTime();
          } else {
            comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
          }
          
          if (comparison !== 0) {
            return sort.direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }
    
    return processedData;
  }, [data, sortConfig, rowPositioning, customRowOrder]);

  // Get ordered columns
  const orderedColumns = columnOrder
    .map(key => columns.find(col => col.key === key))
    .filter(Boolean);

  console.log('CardAdvancedTable Debug:', {
    columns: columns,
    columnOrder: columnOrder,
    orderedColumns: orderedColumns,
    hasActionsColumn: !!orderedColumns.find(col => col.key === 'actions'),
    firstRow: data[0],
    dataLength: data.length,
    firstRowActions: data[0]?.actions,
    firstRowKeys: data[0] ? Object.keys(data[0]) : []
  });

  const densityConfig = DENSITY_CONFIG[density] || DENSITY_CONFIG.normal;

  // Render card content
  const renderCard = (row, index) => {
    console.log(`Card ${index}:`, { 
      hasActions: !!row.actions, 
      actionsType: typeof row.actions,
      actionsContent: row.actions,
      orderedColumns: orderedColumns,
      hasActionsColumn: !!orderedColumns.find(col => col.key === 'actions'),
      rowKeys: Object.keys(row)
    });
    
    return (
      <div 
        key={index}
        className={cn(
          "transition-all duration-200 hover:shadow-md border rounded-lg w-full",
          "border-gray-200 dark:border-gray-700",
          "bg-white dark:bg-gray-800",
          densityConfig.cardPadding,
          "hover:bg-gray-50 dark:hover:bg-gray-700"
        )}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Main content */}
          <div className="flex-1 min-w-0">
            {/* Primary title/name */}
            <div className="text-sm font-medium mb-1 text-gray-900 dark:text-white">
              {row.title || row.name || row.description || `Item ${index + 1}`}
            </div>
            
            {/* Secondary info - show first few fields */}
            <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
              {orderedColumns
                .filter(col => col.key !== 'actions' && col.key !== 'title' && col.key !== 'name' && col.key !== 'description')
                .slice(0, 3)
                .map((column) => (
                  <div key={column.key} className="flex items-center gap-1">
                    <span className="font-medium">{column.header}:</span>
                    <span className="truncate max-w-32">
                      {row[column.key] || '—'}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          
          {/* Right side - Actions and additional info */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Action buttons if actions column exists */}
            {orderedColumns.find(col => col.key === 'actions') && row.actions && (
              <div className={cn(
                "flex items-center",
                spacing,
                actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
              )}>
                {React.isValidElement(row.actions) ? 
                  React.cloneElement(row.actions, {
                    className: cn(
                      row.actions.props.className,
                      spacing,
                      actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                    )
                  }) : 
                  row.actions
                }
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn(
      "relative h-full max-h-full rounded-xl border overflow-hidden",
      "bg-white dark:bg-gray-900",
      "border-gray-200 dark:border-gray-700",
      "shadow-lg",
      className
    )}>
      {/* Header with controls */}
      {showHeader && (
        <div className="sticky top-0 z-10 border-b-2 p-4 bg-gradient-to-r from-blue-600 to-blue-700 border-blue-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-white">
                {tableId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h3>
              <span className="text-sm text-white/80">
                {sortedData.length} items
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Density control */}
              {showDensityControl && (
                <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
                  {Object.entries(DENSITY_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => onDensityChange?.(key)}
                      className={cn(
                        "px-2 py-1 rounded text-xs transition-colors",
                        density === key 
                          ? "bg-white/20 text-white" 
                          : "text-white/70 hover:text-white hover:bg-white/10"
                      )}
                      title={config.name}
                    >
                      {config.name}
                    </button>
                  ))}
                </div>
              )}
              

            </div>
          </div>
          
          {/* Column headers for sorting */}
          <div className={cn("flex items-center gap-4 mt-3", densityConfig.gap)}>
            {orderedColumns
              .filter(col => col.key !== 'actions')
              .map((column) => (
                <button
                  key={column.key}
                  onClick={(e) => handleSort(column.key, e)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer",
                    "text-white/80 hover:text-white hover:bg-white/10",
                    sortConfig.find(s => s.key === column.key) && "bg-white/20 text-white"
                  )}
                  title={`Sort by ${column.header}`}
                >
                  <span className="text-sm">{column.header}</span>
                  
                  {/* Sort indicators */}
                  {(() => {
                    const sortInfo = sortConfig.find(s => s.key === column.key);
                    if (sortInfo) {
                      return (
                        <div className="flex items-center gap-1">
                          <ArrowUpIcon 
                            className={cn(
                              "w-3 h-3 transition-all duration-200",
                              sortInfo.direction === 'desc' && "rotate-180"
                            )} 
                          />
                          
                          {sortConfig.length > 1 && (
                            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-white/20 rounded-full">
                              {sortInfo.priority}
                            </span>
                          )}
                        </div>
                      );
                    } else {
                      return (
                        <SortBothIcon className="w-3 h-3 text-white/50" />
                      );
                    }
                  })()}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Cards container */}
      <div 
        className="p-4 overflow-auto"
        style={{
          maxHeight: showHeader ? `calc(${maxHeight} - 120px)` : maxHeight
        }}
      >
        {sortedData.length > 0 ? (
          <div className="space-y-3">
            {sortedData.map((row, index) => renderCard(row, index))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="mb-4">
              <ClipboardIcon className="w-16 h-16 mx-auto text-gray-400" />
            </div>
            <div className="text-lg font-medium mb-2">No data found</div>
            <div className="text-sm">Try adjusting your filters or search criteria</div>
          </div>
        )}
      </div>
    </div>
  );
};

export { CardAdvancedTable, DENSITY_CONFIG };
